const PGAdapter = require('./../common/pg-adapter')
const { getInsertProperties, getListArgs } = require('../tools/sql-tools')
const v4 = require('uuid/v4')
const COLUMN_TYPES = {
  id: 'uuid',
  buyprice: 'numeric',
  sellprice: 'numeric',
  markup: 'numeric',
  discount: 'numeric',
  month: 'timestamptz',
  date: 'date',
  created_at: 'timestamptz'
}

class PricePGAdapter extends PGAdapter {
  constructor ({pgConnection, username, logger}) {
    const columns = [
      'id',
      'sku',
      'date',
      'buyprice',
      'sellprice',
      'discount',
      'markup',
      'service_id'
    ]
    const virtualColumns = [
      'product',
      'market',
      'month'
    ]
    const opts = {
      addUuid: true,
      strictColumns: false,
      virtualColumns
    }
    super(pgConnection, 'real.price_history', username, columns, 'id', {}, logger, opts)
  }
  async getList ({ ordering = this.idColumn, filter = {}, limit = 50, offset = 0 } = {}) {
    const tableExpression = `
      (
        select
          ph.id,
          p.product_id as sku,
          p.product,
          p.market,
          p.supplier,
          ph.date,
          ph.month,
          ph.buyprice,
          ph.sellprice,
          ph.discount
          from
          real.price_history ph inner join avocado.vw_products p on p.product_id = ph.sku
      ) t
    `
    return PGAdapter.prototype.getList.call(this, {ordering, filter, limit, offset, tableExpression})
  }

  async create (data) {
    if (!data || (typeof data !== 'object')) {
      throw new Error('Object entry required')
    }

    const entries = Array.isArray(data) ? data : [data]
    const createdAt = new Date().toJSON()
    const parsedRows = entries.map(r => ({
      id: v4(),
      sku: r.sku,
      buyprice: r.buyprice,
      sellprice: r.sellprice,
      markup: r.markup,
      date: r.date,
      discount: r.discount,
      created_at: createdAt
    }))

    const { columns, values, placeholders } = getInsertProperties(parsedRows, null, 0, COLUMN_TYPES)
    const distinctSkus = [...new Set(parsedRows.map(r => r.sku))]
    const { args } = getListArgs(distinctSkus, values.length + 1)
    const { rows: [row] } = await this.pgConnection.query(`
      with skus_service_id as (
        select
          s.old_sku_id as sku_id,
          ds.id as service_id
        from avocado.data_productsku s
        left join avocado.data_market m on s.market_id = m.id
        left join avocado.data_service ds on m.fs_service_id = ds.fsid
        where s.old_sku_id in (${args})
      ),
      insert_rows as (
        select
          *
        from (
          values ${placeholders}
        ) v (id, sku, buyprice, sellprice, markup, date, discount, created_at)
      )
      insert into real.price_history (${columns}, service_id)
      select 
        i.id,
        i.sku,
        i.buyprice,
        i.sellprice,
        i.markup,
        i.date,
        i.discount,
        i.created_at,
        sk.service_id
      from insert_rows i
      left join skus_service_id sk on sk.sku_id = i.sku
      returning *
    `, [...values, ...distinctSkus])

    if (row) {
      await this.clearCaches(row)
    }
    return row
  }

  async upsert (data) {
    const res = await PGAdapter.prototype.upsert.call(this, data, {conflictIdColumns: ['sku', 'date']})
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async update (data) {
    const res = await PGAdapter.prototype.update.call(this, data)
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async delete (id) {
    const obj = await this.getOne(id)
    if (obj) {
      const res = await PGAdapter.prototype.delete.call(this, id)
      if (res) {
        await this.clearCaches(obj)
      }
      return res
    }
  }

  async clearCaches ({date, sku}) {
    if (date && sku) {
      this.logger.debug('clearing cached values')
      await this.pgConnection.query(`DELETE FROM real.buyprice_daily WHERE date >= $1 and sku = $2;`, [date, sku])
      await this.pgConnection.query(`DELETE FROM real.sellprice_daily WHERE date >= $1 and sku = $2;`, [date, sku])
      await this.pgConnection.query(`
        UPDATE real.presentation_cost_basis_base
        SET _deleted=true
        WHERE date >= $1
        AND presentation_id = (
          SELECT presentation_id
          FROM avocado.data_productsku
          WHERE old_sku_id = $2
        )
      `, [date, sku])
      await this.pgConnection.query(`
        DELETE
        FROM real.presentation_cost_basis
        WHERE date >= $1
        AND presentation_id = (
          SELECT presentation_id
          FROM avocado.data_productsku
          WHERE old_sku_id = $2
        )
      `, [date, sku])
    }
  }

  /**
   * @param {Object} params
   * @param {string[]} params.skus
   * @param {string} params.locationFsid
   * @param {string} params.serviceId
   * @param {string|Date} params.referenceDate
   * @returns {Promise<{referenceDate: string, prices: any[]}>}
   */
  async getPriceList ({ skus, locationFsid, serviceId, referenceDate }) {
    const normalizedSkus = Array.isArray(skus) && skus.length > 0 ? skus.map(sku => sku.replace('product:', '')) : null
    const normalizedReferenceDate = referenceDate || null
    const queryParams = [normalizedSkus, normalizedReferenceDate]

    let constraintWhereClause = `WHERE ph.service_id IS NULL AND ph.location_id IS NULL`
    if (locationFsid && !serviceId) {
      queryParams.push(locationFsid)
      constraintWhereClause = `WHERE ph.location_id = l.uuid AND ph.service_id IS NULL and l.fsid = $3`
    }
    if (serviceId && !locationFsid) {
      queryParams.push(serviceId)
      constraintWhereClause = `WHERE ph.location_id IS NULL AND ph.service_id = s.id and s.fsid = $3`
    }
    if (serviceId && locationFsid) {
      queryParams.push(locationFsid, serviceId)
      constraintWhereClause = `WHERE location_id = l.uuid and service_id = s.id and l.fsid = $3 and s.fsid = $4`
    }

    const query = `
      with product_base as (
      select
          p.old_sku_id as sku,
          m.product_market_code,
          m.aliases,
          m.id as market_id,
          p.vat
        from avocado.data_productsku p
        left join avocado.data_market m on p.market_id = m.id
        where ($1::text[] is null or p.old_sku_id = any($1::text[]))
      ),
      product_prices as (
        select
          ph.*,
          pb.aliases,
          pb.market_id
        from product_base pb
        left join real.price_history ph on ph.sku = pb.sku
        left join avocado.data_service s on ph.service_id = s.id
        left join avocado.data_location l on l.uuid = ph.location_id
        ${constraintWhereClause}
      ),
      markup as (
        select distinct on (sku)
          p.id as markup_id,
          p.sku,
          p.sellprice,
          p.markup,
          p.date,
          mp.financing_multiplier,
          mp.delivery_multiplier
        from product_prices p
        left join avocado.data_markupmultiplierhistory mp on p.date::date = mp.date::date and (mp.market_id = any (p.aliases) or mp.market_id = p.market_id)
        where coalesce(p.markup, 0) > 0 and p.date <= coalesce($2, now()) and mp.id is not null
        order by p.sku, p.date desc
      ),
      sell_prices as (
        select distinct on (sku)
          id as sell_price_id,
          sku,
          date,
          sellprice,
          discount
        from product_prices
        where sellprice is not null and date <= coalesce($2, now())
        order by sku, date desc
      ),
      buy_prices as (
        select distinct on (sku)
          id as buy_price_id,
          sku,
          date,
          buyprice,
          discount
        from product_prices
        where buyprice is not null and date <= coalesce($2, now())
        order by sku, date desc
      ),
      prices as (
        select
          s.sell_price_id as "sellpriceId",
          b.buy_price_id as "buypriceId",
          concat('product:', pb.sku) as sku,     
          pb.vat,
          avocado.round_half_even(s.sellprice , 2)::float as sellprice,
          avocado.round_half_even(b.buyprice , 2)::float as buyprice,
          avocado.round_half_even(s.discount , 2) as discount,
          avocado.round_half_even(mp.markup * mp.financing_multiplier * (mp.sellprice / (1 + mp.markup + mp.financing_multiplier * mp.markup + mp.delivery_multiplier * mp.markup)), 2) as "financingMarkup",
          avocado.round_half_even(mp.markup * mp.delivery_multiplier * (mp.sellprice / (1 + mp.markup + mp.financing_multiplier * mp.markup + mp.delivery_multiplier * mp.markup)), 2) as "deliveryMarkup"
        from product_base pb
        left join sell_prices s on pb.sku = s.sku
        left join buy_prices b on pb.sku = b.sku
        left join markup mp on pb.sku = mp.sku
      )
      select
        coalesce((select jsonb_agg(p) from (select * from prices) p), '[]'::jsonb) as prices,
        coalesce($2, now()) as "referenceDate"
    `
    const { rows } = await this.pgConnection.query(query, queryParams)
    const response = rows[0]
    response.referenceDate = new Date(response.referenceDate).toISOString()
    return response
  }
}

module.exports = {PricePGAdapter}
